import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CAREER_UNIT_KINDS,
  type CareerUnit,
  type CareerUnitKind,
  type CreateCareerUnit,
  type UpdateCareerUnitPatch,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export type ByKind = Partial<Record<CareerUnitKind, number>>;

export interface CorpusResponse {
  units: CareerUnit[];
  by_kind: ByKind;
  // Re-introduced in wave 4 — wave 3 deferred this until resume_versions
  // had anything to count. Keys are career_units.id, values are usage counts.
  // Empty `{}` until the user runs the tailor.
  used_in_versions: Record<string, number>;
}

export function useCorpus(kind?: CareerUnitKind) {
  const qs = kind ? `?kind=${kind}` : "";
  return useQuery({
    queryKey: ["corpus", kind ?? "all"],
    queryFn: () => api<CorpusResponse>(`/api/corpus${qs}`),
    staleTime: 30_000,
  });
}

export function useCorpusUnit(id: string | null) {
  return useQuery({
    queryKey: ["corpus", "unit", id],
    enabled: !!id,
    queryFn: () => api<{ unit: CareerUnit }>(`/api/corpus/${id}`),
    staleTime: 30_000,
  });
}

export interface CorpusError {
  status: number;
  error: string;
  message?: string;
  existing_count?: number;
}

function toCorpusError(err: unknown): CorpusError {
  if (err instanceof ApiError) {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error:
        (typeof payload.error === "string" ? payload.error : err.message) ??
        "request_failed",
      message:
        typeof payload.message === "string" ? payload.message : undefined,
      existing_count:
        typeof payload.existing_count === "number"
          ? payload.existing_count
          : undefined,
    };
  }
  return { status: 0, error: "network_error" };
}

export function useCreateCorpusUnit() {
  const qc = useQueryClient();
  return useMutationToast<
    { unit: CareerUnit },
    CorpusError,
    CreateCareerUnit
  >({
    mutationFn: async (input) => {
      try {
        return await api<{ unit: CareerUnit }>("/api/corpus", {
          method: "POST",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toCorpusError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: (data) => `Added ${kindLabel(data.unit.kind)}.`,
    errorPrefix: "Add failed",
  });
}

export function useUpdateCorpusUnit() {
  const qc = useQueryClient();
  return useMutationToast<
    { unit: CareerUnit },
    CorpusError,
    { id: string; patch: UpdateCareerUnitPatch }
  >({
    mutationFn: async ({ id, patch }) => {
      try {
        return await api<{ unit: CareerUnit }>(`/api/corpus/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        });
      } catch (err) {
        throw toCorpusError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: "Saved.",
    errorPrefix: "Save failed",
  });
}

export function useDeleteCorpusUnit() {
  const qc = useQueryClient();
  return useMutationToast<{ ok: true }, CorpusError, string>({
    mutationFn: async (id) => {
      try {
        return await api<{ ok: true }>(`/api/corpus/${id}`, {
          method: "DELETE",
        });
      } catch (err) {
        throw toCorpusError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: "Deleted.",
    errorPrefix: "Delete failed",
  });
}

export interface ImportResumeResult {
  imported: number;
  by_kind: ByKind;
}

export function useImportResume() {
  const qc = useQueryClient();
  return useMutationToast<ImportResumeResult, CorpusError, string>({
    mutationFn: async (resumeId) => {
      try {
        return await api<ImportResumeResult>(
          `/api/corpus/import-from-resume/${resumeId}`,
          { method: "POST" },
        );
      } catch (err) {
        const e = toCorpusError(err);
        if (e.status === 409 && e.existing_count) {
          e.message = `Already imported ${e.existing_count} unit${
            e.existing_count === 1 ? "" : "s"
          } — delete those first to re-import.`;
        }
        throw e;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["corpus"] });
    },
    success: (data) =>
      `Imported ${data.imported} unit${data.imported === 1 ? "" : "s"} — embedding in the background.`,
    errorPrefix: "Import failed",
  });
}

export const KIND_LABELS: Record<CareerUnitKind, string> = {
  bullet: "bullet",
  project: "project",
  role: "role",
  skill: "skill",
  summary: "summary",
  education: "education",
  cert: "certification",
  award: "recognition",
  story: "story",
};

export function kindLabel(kind: CareerUnitKind): string {
  return KIND_LABELS[kind];
}

export function totalUnits(by_kind: ByKind): number {
  let n = 0;
  for (const k of CAREER_UNIT_KINDS) n += by_kind[k] ?? 0;
  return n;
}
